import { axiosInstance } from "@/lib/axios-instance";
import { DealInfoSchema } from "@/lib/schema/deal-schema";
import { z } from "zod";
import { useDealStatsStore } from "@/store/deal-stats-store";
import { getErrorMessage } from "@/lib/api-error";

// Define response schema using the existing DealInfoSchema
const DealInfoResponseSchema = z.object({
  deal_info: z.array(DealInfoSchema).optional(),
  message: z.string().optional(),
  status: z.string().optional(),
});

type DealInfoResponse = z.infer<typeof DealInfoResponseSchema>;

export const aiApi = {
  summarizeWebsite: async ({
    companyId,
    opportunityId,
    url,
  }: {
    companyId: string;
    opportunityId: string;
    url: string;
  }) => {
    const response = await axiosInstance.post(
      "/summarize_website",
      {
        url: url,
      },
      {
        params: {
          company_id: companyId,
          opportunity_id: opportunityId,
        },
      }
    );

    // Refresh deal stats after summarizing website
    const { fetchDealStats } = useDealStatsStore.getState();
    fetchDealStats(opportunityId);

    return response.data;
  },
  deleteAiGeneration: async ({ generationId }: { generationId: string }) => {
    const response = await axiosInstance.delete(
      `/delete_ai_generation/${generationId}`
    );

    // Note: Cannot refresh deal stats here as we don't have opportunityId
    // This would need to be handled at the calling component level

    return response.data;
  },
  deleteWebsiteSummaryV2: async ({
    reportId,
    companyId,
    workspaceId,
  }: {
    reportId: string;
    companyId: string;
    workspaceId: string;
  }) => {
    const response = await axiosInstance.post(
      `/delete_website_summary_v2/${reportId}`,
      {
        company_id: companyId,
        workspaceId: workspaceId,
      }
    );
    return response.data;
  },
  deleteLinkedInInfo: async ({
    reportId,
    companyId,
    workspaceId,
  }: {
    reportId: string;
    companyId: string;
    workspaceId: string;
  }) => {
    const response = await axiosInstance.post(
      `/delete_linkedin_info/${reportId}`,
      {
        company_id: companyId,
        workspaceId: workspaceId,
      }
    );
    return response.data;
  },
  deleteMarketResearch: async ({
    reportId,
    companyId,
    workspaceId,
  }: {
    reportId: string;
    companyId: string;
    workspaceId: string;
  }) => {
    const response = await axiosInstance.post(
      `/delete_market_research/${reportId}`,
      {
        company_id: companyId,
        workspaceId: workspaceId,
      }
    );
    return response.data;
  },
  getWebsiteDataSufficiency: async ({
    companyId,
    opportunityId,
    applicationInfo,
  }: {
    companyId: string;
    opportunityId: string;
    applicationInfo: object;
  }) => {
    const response = await axiosInstance.post(
      "/fetch_data_sufficiency_score",
      {
        data: {
          applicationObject: applicationInfo,
        },
      },
      {
        params: {
          company_id: companyId,
          opportunity_id: opportunityId,
        },
      }
    );

    // Refresh deal stats after fetching data sufficiency score
    const { fetchDealStats } = useDealStatsStore.getState();
    fetchDealStats(opportunityId);

    return response.data;
  },
  getMarketIntelligence: async ({
    companyId,
    opportunityId,
    applicationInfo,
  }: {
    companyId: string;
    opportunityId: string;
    applicationInfo: object;
  }) => {
    const response = await axiosInstance.post(
      "/fetch_market_intelligence",
      {
        data: {
          applicationObject: applicationInfo,
        },
      },
      {
        params: {
          company_id: companyId,
          opportunity_id: opportunityId,
        },
      }
    );

    // Refresh deal stats after fetching market intelligence
    const { fetchDealStats } = useDealStatsStore.getState();
    fetchDealStats(opportunityId);

    return response.data;
  },
  getInsights: async ({
    companyId,
    opportunityId,
    applicationInfo,
  }: {
    companyId: string;
    opportunityId: string;
    applicationInfo: object;
  }) => {
    const response = await axiosInstance.post(
      "/fetch_insights",
      {
        data: {
          applicationObject: applicationInfo,
        },
      },
      {
        params: {
          company_id: companyId,
          opportunity_id: opportunityId,
        },
      }
    );

    // Refresh deal stats after fetching insights
    const { fetchDealStats } = useDealStatsStore.getState();
    fetchDealStats(opportunityId);

    return response.data;
  },
  getTearSheet: async ({
    companyId,
    opportunityId,
    applicationInfo,
  }: {
    companyId: string;
    opportunityId: string;
    applicationInfo: object;
  }) => {
    const response = await axiosInstance.post(
      "/fetch_tearsheet",
      {
        data: {
          applicationObject: applicationInfo,
        },
      },
      {
        params: {
          company_id: companyId,
          opportunity_id: opportunityId,
        },
      }
    );

    // Refresh deal stats after fetching tear sheet
    const { fetchDealStats } = useDealStatsStore.getState();
    fetchDealStats(opportunityId);

    return response.data;
  },
  startCustomizeUiTearsheet: async ({
    companyId,
    opportunityId,
    versionId,
    instructions,
  }: {
    companyId: string;
    opportunityId: string;
    /** Present when refining an existing version; omit for a new generation. */
    versionId?: string;
    instructions?: string;
  }) => {
    if (!companyId || !opportunityId) {
      // Fail fast in the client with a clear message instead of hitting the
      // BE and getting a generic 400 — surfaces caller-side wiring bugs.
      throw new Error(
        `startCustomizeUiTearsheet: missing ids (companyId=${String(
          companyId,
        )}, opportunityId=${String(opportunityId)})`,
      );
    }
    const response = await axiosInstance.post<{ ok: true }>(
      "/customize_ui_tearsheet/start",
      {
        company_id: companyId,
        opportunity_id: opportunityId,
        version_id: versionId,
        instructions,
      },
    );

    // Refresh deal stats after kicking off a new generation.
    const { fetchDealStats } = useDealStatsStore.getState();
    fetchDealStats(opportunityId);

    return response.data;
  },

  updateTearsheetV2Structure: async ({
    documentId,
    versionId,
    uiData,
  }: {
    documentId: string;
    versionId: string;
    uiData: unknown;
  }) => {
    if (!documentId || !versionId || !uiData) {
      throw new Error(
        `updateTearsheetV2Structure: missing required fields (documentId=${String(
          documentId,
        )}, versionId=${String(versionId)})`,
      );
    }
    const response = await axiosInstance.patch<{ ok: true }>(
      "/customize_ui_tearsheet/update_structure",
      {
        document_id: documentId,
        version_id: versionId,
        ui_data: uiData,
      },
    );
    return response.data;
  },

  getDealBrief: async ({
    companyId,
    opportunityId,
    applicationInfo,
  }: {
    companyId: string;
    opportunityId: string;
    applicationInfo: object;
  }) => {
    const response = await axiosInstance.post(
      `/fetch_company_summary`,
      {
        data: {
          applicationObject: applicationInfo,
        },
      },
      {
        params: {
          company_id: companyId,
          opportunity_id: opportunityId,
        },
      }
    );

    // Refresh deal stats after fetching deal brief
    const { fetchDealStats } = useDealStatsStore.getState();
    fetchDealStats(opportunityId);

    return response.data;
  },
  getStartupScore: async ({
    companyId,
    opportunityId,
    applicationInfo,
  }: {
    companyId: string;
    opportunityId: string;
    applicationInfo: object;
  }) => {
    const response = await axiosInstance.post(
      `/fetch_company_score`,
      {
        data: {
          applicationObject: applicationInfo,
        },
      },
      {
        params: {
          company_id: companyId,
          opportunity_id: opportunityId,
        },
      }
    );

    // Refresh deal stats after fetching startup score
    const { fetchDealStats } = useDealStatsStore.getState();
    fetchDealStats(opportunityId);

    return response.data;
  },
  getMetrics: async ({
    companyId,
    opportunityId,
    applicationObject,
  }: {
    companyId: string;
    opportunityId: string;
    applicationObject: object;
  }) => {
    const response = await axiosInstance.post(
      `/fetch_kpi_data`,
      {
        data: {
          applicationInfo: applicationObject,
        },
      },
      {
        params: { company_id: companyId, opportunity_id: opportunityId },
      }
    );

    // Refresh deal stats after fetching metrics
    const { fetchDealStats } = useDealStatsStore.getState();
    fetchDealStats(opportunityId);

    return response.data;
  },
  getGapAnalysis: async ({
    companyId,
    opportunityId,
    applicationObject,
  }: {
    companyId: string;
    opportunityId: string;
    applicationObject: object;
  }) => {
    const response = await axiosInstance.post(
      `/fetch_gap_analysis`,
      {
        data: {
          applicationInfo: applicationObject,
        },
      },
      {
        params: { company_id: companyId, opportunity_id: opportunityId },
      }
    );

    // Refresh deal stats
    const { fetchDealStats } = useDealStatsStore.getState();
    fetchDealStats(opportunityId);

    return response.data;
  },
  getDealFit: async ({
    companyId,
    opportunityId,
    applicationObject,
  }: {
    companyId: string;
    opportunityId: string;
    applicationObject: object;
  }) => {
    const response = await axiosInstance.post(
      `/fetch_deal_fit`,
      {
        data: {
          applicationInfo: applicationObject,
        },
      },
      {
        params: { company_id: companyId, opportunity_id: opportunityId },
      }
    );

    // Refresh deal stats
    const { fetchDealStats } = useDealStatsStore.getState();
    fetchDealStats(opportunityId);

    return response.data;
  },
  generateValuationsReport: async (
    workspaceId: string,
    company_id: string
  ): Promise<any> => {
    const response = await axiosInstance.post(`/generate_valuations_report`, {
      opportunity_id: workspaceId,
      company_id,
    });

    // Refresh deal stats after generating valuations report
    const { fetchDealStats } = useDealStatsStore.getState();
    fetchDealStats(workspaceId);

    return response.data;
  },
  deleteValuationsReport: async (
    reportId: string,
    companyId: string,
    workspaceId: string
  ): Promise<any> => {
    const response = await axiosInstance.post(
      `/delete_valuations_report/${reportId}`,
      {
        company_id: companyId,
        workspaceId,
      }
    );

    // Refresh deal stats after deleting valuations report
    const { fetchDealStats } = useDealStatsStore.getState();
    fetchDealStats(workspaceId);

    return response.data;
  },
  getDealInfo: async ({
    companyId,
    opportunityId,
    applicationInfo,
  }: {
    companyId: string;
    opportunityId: string;
    applicationInfo: object;
  }): Promise<DealInfoResponse> => {
    const response = await axiosInstance.post(
      "/get_deal_info",
      {
        data: {
          applicationInfo,
        },
      },
      {
        params: {
          company_id: companyId,
          opportunity_id: opportunityId,
        },
      }
    );

    // Refresh deal stats after fetching deal info
    const { fetchDealStats } = useDealStatsStore.getState();
    fetchDealStats(opportunityId);

    return DealInfoResponseSchema.parse(response.data);
  },
  getValuations: async ({
    companyId,
    opportunityId,
  }: {
    companyId: string;
    opportunityId: string;
  }) => {
    const response = await axiosInstance.get(
      `/get_reports?company_id=${companyId}&opportunity_id=${opportunityId}&report_type=valuations`
    );
    return response.data;
  },
  getDecileBenchmarks: async ({
    companyId,
    opportunityId,
  }: {
    companyId: string;
    opportunityId: string;
  }) => {
    const response = await axiosInstance.get(
      `/get_decile_benchmarks?company_id=${companyId}&opportunity_id=${opportunityId}`
    );
    return response.data;
  },
  sendChatMessage: async ({
    companyId,
    opportunityId,
    question,
    applicationObject,
    userId,
  }: {
    companyId: string;
    opportunityId: string;
    question: string;
    applicationObject: object;
    userId: string;
  }) => {
    if (!userId) {
      throw new Error("User ID is required");
    }

    const response = await axiosInstance.post("/fetch_summarize", {
      companyId,
      opportunityId,
      applicationObject,
      userId,
      question,
    });

    // Refresh deal stats after sending chat message
    const { fetchDealStats } = useDealStatsStore.getState();
    fetchDealStats(opportunityId);

    return response.data;
  },
  createChatThread: async ({
    companyId,
    opportunityId,
  }: {
    companyId: string;
    opportunityId: string;
  }) => {
    const response = await axiosInstance.post("/chat/threads/create", {
      companyId,
      opportunityId,
    });
    return response.data;
  },
  getChatThread: async (threadId: string) => {
    const response = await axiosInstance.get(`/chat/threads/${threadId}`);
    return response.data;
  },
  getChatThreads: async ({
    companyId,
    opportunityId,
  }: {
    companyId: string;
    opportunityId: string;
  }) => {
    const response = await axiosInstance.get("/chat/threads", {
      params: {
        companyId,
        opportunityId,
      },
    });
    return response.data;
  },
  getAvailableModels: async (): Promise<string[]> => {
    const response = await axiosInstance.get("/chat/models/available");
    return response.data.models || [];
  },
  sendChatMessageStream: async ({
    chatId,
    userQuery,
    companyId,
    opportunityId,
    model,
    onChunk,
    onComplete,
    onError,
    onToolCall,
    onToolResult,
  }: {
    chatId: string;
    userQuery: string;
    companyId: string;
    opportunityId: string;
    model?: string;
    onChunk?: (chunk: string) => void;
    onComplete?: (fullContent: string) => void;
    onError?: (error: Error) => void;
    onToolCall?: (toolData: { name: string;[key: string]: unknown }) => void;
    onToolResult?: (toolData: {
      name: string;
      content: string;
      [key: string]: unknown;
    }) => void;
  }): Promise<string> => {
    const streamId = `stream-${Date.now()}-${Math.random()
      .toString(36)
      .substring(2, 9)}`;
    console.log(
      `[AI-API] [${streamId}] sendChatMessageStream STARTED, chatId=${chatId}`
    );

    let isCompleted = false;
    let chunkCount = 0;

    try {
      // Get the auth token similar to axios interceptor
      let authToken = "";
      try {
        // First try Clerk token
        if (window.Clerk?.session) {
          authToken = (await window.Clerk.session.getToken()) || "";
        }
        // Fallback to JWT if no Clerk token
        if (!authToken) {
          const { authService } = await import("@/services/auth-service");
          authToken = (await authService.getValidAccessToken()) || "";
        }
      } catch (tokenError) {
        console.error("Failed to get auth token:", tokenError);
      }

      // Use native fetch for streaming
      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };
      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      }

      const response = await fetch(
        `${axiosInstance.defaults.baseURL}/chat/message/stream`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            chat_id: chatId,
            user_query: userQuery,
            companyId,
            opportunityId,
            ...(model && { model }),
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          getErrorMessage(
            { response: { status: response.status } },
            `HTTP error! status: ${response.status}`
          )
        );
      }

      if (!response.body) {
        throw new Error("Response body is null");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = "";
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6).trim();
            if (data === "[DONE]") {
              console.log(
                `[AI-API] [${streamId}] [DONE] received, isCompleted=${isCompleted}, chunkCount=${chunkCount}, fullContent length=${fullContent.length}`
              );
              if (onComplete && !isCompleted) {
                isCompleted = true;
                console.log(
                  `[AI-API] [${streamId}] Calling onComplete() with fullContent length=${fullContent.length}`
                );
                onComplete(fullContent);
                console.log(
                  `[AI-API] [${streamId}] onComplete() called, returning`
                );
              } else {
                console.log(
                  `[AI-API] [${streamId}] SKIPPED onComplete - already completed or no callback`
                );
              }
              return fullContent;
            }

            try {
              const parsed = JSON.parse(data);

              // Log all nodes for analysis
              console.log(
                `[AI-API] [${streamId}] Node received:`,
                JSON.stringify(parsed, null, 2)
              );

              if (parsed.error) {
                const error = new Error(parsed.error);
                if (onError) onError(error);
                throw error;
              }

              // Handle tools nodes (tool invocation - when a tool is being called)
              if (parsed.node === "tools") {
                // Extract tool name from name field (e.g., "find_context_by_query")
                const toolName = parsed.name || "tool";
                console.log(
                  `[AI-API] [${streamId}] Tool call detected: ${toolName}, content length: ${parsed.content?.length || 0
                  }, full object:`,
                  parsed
                );
                if (onToolCall) {
                  console.log(
                    `[AI-API] [${streamId}] Calling onToolCall with name=${toolName}, content length=${parsed.content?.length || 0
                    }`
                  );
                  // Pass the full parsed object with all fields (name, reasoning, etc.)
                  onToolCall({ ...parsed, name: toolName });
                } else {
                  console.log(
                    `[AI-API] [${streamId}] onToolCall callback not provided!`
                  );
                }
                continue; // Don't process tools nodes as content
              }

              // Handle tool nodes (tool result - when a tool returns its output)
              if (parsed.node === "tool") {
                const toolName = parsed.name || "tool";
                console.log(
                  `[AI-API] [${streamId}] Tool result received: ${toolName}, content length: ${parsed.content?.length || 0
                  }`,
                  parsed
                );
                if (onToolResult) {
                  // Pass the full parsed object with name, content, and any other fields
                  onToolResult({
                    ...parsed,
                    name: toolName,
                    content: parsed.content || "",
                  });
                }
                continue; // Don't process tool result nodes as assistant content
              }

              // Log assistant nodes
              if (parsed.node === "assistant") {
                console.log(
                  `[AI-API] [${streamId}] Assistant node received, content length: ${parsed.content?.length || 0
                  }`
                );
              }

              // Log any other node types
              if (
                parsed.node &&
                parsed.node !== "assistant" &&
                parsed.node !== "tools" &&
                parsed.node !== "tool"
              ) {
                console.log(
                  `[AI-API] [${streamId}] Unknown node type: ${parsed.node}`,
                  parsed
                );
              }

              // Extract content from assistant nodes
              const content = parsed.content || "";
              if (content && !isCompleted) {
                chunkCount++;
                fullContent += content;
                console.log(
                  `[AI-API] [${streamId}] onChunk #${chunkCount} called, content length=${content.length}, total length=${fullContent.length}, isCompleted=${isCompleted}`
                );
                if (onChunk) onChunk(content);
              } else if (content && isCompleted) {
                console.log(
                  `[AI-API] [${streamId}] SKIPPED chunk (already completed), content length=${content.length}`
                );
              }
            } catch (parseError) {
              // If it's not JSON, treat as plain text chunk
              if (data.trim()) {
                fullContent += data;
                if (onChunk) onChunk(data);
              }
            }
          }
        }
      }

      // Process remaining buffer
      if (buffer.trim() && !isCompleted) {
        console.log(
          `[AI-API] [${streamId}] Processing remaining buffer, isCompleted=${isCompleted}`
        );
        if (buffer.startsWith("data: ")) {
          const data = buffer.slice(6).trim();
          if (data !== "[DONE]") {
            try {
              const parsed = JSON.parse(data);
              // Handle tools nodes in buffer
              if (parsed.node === "tools") {
                const toolName = parsed.name || "tool";
                console.log(
                  `[AI-API] [${streamId}] Tool call detected (from buffer): ${toolName}`
                );
                if (onToolCall) {
                  onToolCall(toolName);
                }
                return fullContent; // Don't process tools nodes as content
              }
              const content = parsed.content || "";
              if (content) {
                chunkCount++;
                fullContent += content;
                console.log(
                  `[AI-API] [${streamId}] onChunk #${chunkCount} called (from buffer), isCompleted=${isCompleted}`
                );
                if (onChunk) onChunk(content);
              }
            } catch {
              fullContent += buffer;
              if (onChunk) onChunk(buffer);
            }
          }
        }
      }

      if (!isCompleted) {
        console.log(
          `[AI-API] [${streamId}] Stream ended naturally, calling onComplete(), isCompleted=${isCompleted}, chunkCount=${chunkCount}, fullContent length=${fullContent.length}`
        );
        isCompleted = true;
        if (onComplete) {
          onComplete(fullContent);
          console.log(
            `[AI-API] [${streamId}] onComplete() called from natural end`
          );
        }
      } else {
        console.log(
          `[AI-API] [${streamId}] Stream ended but already completed, skipping onComplete()`
        );
      }
      return fullContent;
    } catch (error) {
      const err =
        error instanceof Error ? error : new Error("Unknown streaming error");
      if (onError) onError(err);
      throw err;
    }
  },
  getQuestionnaireAnswer: async ({
    companyId,
    opportunityId,
    question,
    applicationObject,
  }: {
    companyId: string;
    opportunityId: string;
    question: string;
    applicationObject: object;
  }) => {
    const response = await axiosInstance.post("/fetch_questionnaire_answer", {
      companyId,
      opportunityId,
      applicationObject,
      question,
    });

    // Refresh deal stats after getting questionnaire answer
    const { fetchDealStats } = useDealStatsStore.getState();
    fetchDealStats(opportunityId);

    return response.data;
  },
};

interface SaveTearSheetProps {
  content: Record<string, string | string[]>;
  documentId: string;
}

export const saveTearSheet = async ({
  content,
  documentId,
}: SaveTearSheetProps) => {
  const response = await axiosInstance.post("/save_tearsheet", {
    content,
    documentId,
  });

  // Note: Cannot refresh deal stats here as we don't have opportunityId
  // This would need to be handled at the calling component level

  return response.data;
};
